import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'transfix-ui/components/Button'
import { Box } from 'transfix-ui/core'
import { DispatcherAutosuggest } from 'apps/fleet-planner/components/atoms'
import { AddDispatcherModal } from 'apps/fleet-planner/components/modals/AddDispatcherModal'
import { IDispatcher, ILoad, ITeamMember } from 'apps/fleet-planner/types'
import { fetchDispatchers } from 'apps/fleet-planner/utils/actions/dispatchers'
import { dispatchersListSelector, selectCarriersList } from 'apps/fleet-planner/utils/selectors'
import { Toast, ToastState, useToastState } from 'transfix-ui'
import { teamMembertoDispatcher } from 'utils/teamMemberHelpers'

export interface IDispatcherProps {
  value: Partial<ILoad>
  selectedMotorCarrierId?: number
  onChange: (change: Partial<ILoad>) => void
  error?: string // errors.dispatcherId

  /** @default true */
  large?: boolean

  /** @default true */
  required?: boolean

  /** @default true */
  shown?: boolean
}

export const Dispatcher = memo<IDispatcherProps>(
  ({
    error: errorProp,
    large: largeProp = true,
    onChange: onChangeProp,
    required: requiredProp = true,
    selectedMotorCarrierId,
    shown: shownProp = true,
    value: valueProp,
  }) => {
    const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
    const dispatch = useDispatch()
    const carriers = useSelector(selectCarriersList)
    const loadedDispatchers = useSelector(dispatchersListSelector)
    const [dispatchers, setDispatchers] = useState<IDispatcher[]>(loadedDispatchers)
    const [isAddModalVisible, setIsAddModalVisible] = useState<boolean>(false)

    const getDispatcherById = useCallback(
      (newDispatcherId?: string | number) =>
        (dispatchers || []).find(d => Number(d.id) === Number(newDispatcherId)),
      [dispatchers]
    )

    // Update dispatcher values in parent load
    const dispatcherChangeHandler = useCallback(
      (update?: Partial<IDispatcher>) => {
        const updatedDispatcherId = update?.id
        onChangeProp({
          dispatcherId: updatedDispatcherId,
          dispatcher: getDispatcherById(updatedDispatcherId),
        })
      },
      [getDispatcherById, onChangeProp]
    )

    const addEntityHandler = useCallback(() => {
      setIsAddModalVisible(true)
    }, [])

    const submitHandler = useCallback(
      (
        toastState: Omit<ToastState, 'toastOpen'>,
        createdTeamMember?: Omit<ITeamMember, 'motorCarrierDetails'>
      ) => {
        openToast(toastState)
        const newDispatcher = teamMembertoDispatcher(createdTeamMember)
        if (newDispatcher) {
          setDispatchers(existingDispatchers => [...(existingDispatchers || []), newDispatcher])
        }
        onChangeProp({
          dispatcherId: newDispatcher?.id,
          dispatcher: newDispatcher,
        })
      },
      [onChangeProp, openToast]
    )

    const selectedCarrier = useMemo(
      () =>
        selectedMotorCarrierId
          ? carriers.find(carrier => carrier.id === selectedMotorCarrierId)
          : undefined,
      [carriers, selectedMotorCarrierId]
    )

    // fetch dispatchers on mount
    useEffect(() => {
      dispatch(fetchDispatchers())
    }, [])

    useEffect(() => {
      setDispatchers(loadedDispatchers)
    }, [loadedDispatchers])

    return (
      <Box mb={4} data-test='load-dispatcher'>
        {/* AutoSuggest */}
        {shownProp && (
          <DispatcherAutosuggest
            value={valueProp.dispatcher}
            dispatchers={dispatchers}
            onAddEntity={addEntityHandler}
            onChange={dispatcherChangeHandler}
            error={isAddModalVisible ? undefined : errorProp}
            large={largeProp}
            required={requiredProp}
            disabled={isAddModalVisible}
          />
        )}
        {/* Add New Dispatcher Button */}
        {shownProp && !valueProp.dispatcher?.id && (
          <Button
            className='btn-text-link-b mt1'
            data-test='add-new-dispatcher-cta'
            onClick={() => setIsAddModalVisible(true)}>
            + Add new dispatcher
          </Button>
        )}
        {/* Add New Dispatcher Modal */}
        {isAddModalVisible && (
          <AddDispatcherModal
            selectedCarrier={selectedCarrier}
            onClose={() => setIsAddModalVisible(false)}
            onSubmit={submitHandler}
          />
        )}
        <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      </Box>
    )
  }
)
